import groupBy from 'lodash/groupBy';
import minBy from 'lodash/minBy';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';
import upperFirst from 'lodash/upperFirst';

import { DEFAULT_CURRENCY } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsFormatters';

import { formatDateShort } from '~/services/TimeService';

import featureToggle from '~/utils/featureToggle';
import { pluralizeToString } from '~/utils/stringUtils';

import { audMoney } from '../constants';
import {
  COPY_SECTIONS,
  type CopySection,
  type CopySectionDetails,
  replacesWarning,
} from '../copyFromPrevious/copyFromPreviousModel';
import type { BlackoutFormValues } from '../tabs/blackouts/types';
import { buildEgsRecord, defaultEgsCategoryDraft } from '../tabs/egs/egsModel';
import { EGS_CATS } from '../tabs/egs/fields';
import { nameKey } from '../tabs/egs/previousEgs';
import type { EgsCategoryMeta, EgsDraft, EgsDraftInput, EgsGuestCategoryKey } from '../tabs/egs/types';
import { defaultApplyTo, findReusableInclusionAsset } from '../tabs/inclusions/inclusionsModel';
import type { InclusionFormValues } from '../tabs/inclusions/types';
import { BASIS_OPTIONS, defaultTierName, validateOverrides } from '../tabs/overrides/overridesValidation';
import type { OverrideSet } from '../tabs/overrides/types';
import { BASIC_CATEGORY_INDEX } from '../tabs/packageOptions/packageCategories';
import type { PreviousDealOptions } from '../tabs/packageOptions/previousDealOptions';
import { DEFAULT_OCCUPANCY } from '../tabs/packageOptions/previousPackageVariants';
import type { CreatedAsset, OptionState, RoomAssetLink } from '../tabs/packageOptions/types';
import {
  LOCATION_FIELDS,
  type TravelWindow,
  applyLocationChange,
  findLocationCountry,
  staleRangeReason,
} from '../tabs/propertySetup/propertySetupModel';
import type { PropertySetupValues } from '../tabs/propertySetup/types';
import {
  NEW_PERIOD_DEFAULTS,
  SEASON_GRIDS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  createEmptySurchargeSession,
  travelMonthsFrom,
} from '../tabs/surcharges/surchargeGridsModel';
import type {
  SurchargeGrids,
  SurchargePeriodFormValues,
  SurchargeSessionState,
  WeekdayAmounts,
} from '../tabs/surcharges/types';

import { prefillRoomChoices } from './roomMapping';
import { andList, periodDates } from './sheetCells';
import type {
  ImportIssue,
  ImportSection,
  ParsedBlackout,
  ParsedDealOption,
  ParsedEgsRoom,
  ParsedInclusion,
  ParsedOverrideTier,
  ParsedOverrides,
  ParsedPropertySetup,
  ParsedSurchargeGrid,
  ParsedSurchargePeriod,
  ParsedWorkbook,
} from './types';

export type ImportSectionKey =
  | Extract<CopySection, 'dealOptions' | 'inclusions' | 'surcharges' | 'extraGuestSurcharges' | 'blackouts'>
  | 'propertySetup'
  | 'overrides';

// Property Setup comes first: the currency picked there decides what the other sections load.
const IMPORT_SECTION_KEYS: Array<ImportSectionKey> = [
  'propertySetup',
  'dealOptions',
  'inclusions',
  'overrides',
  'surcharges',
  'extraGuestSurcharges',
  'blackouts',
];

// Copy from previous copies neither Property Setup nor overrides, and the import keeps the tab's infant surcharges.
const OWN_SECTIONS: Partial<Record<ImportSectionKey, { label?: string; replaces: string }>> = {
  propertySetup: { label: 'Property Setup', replaces: 'Property Setup fields you tick' },
  overrides: { label: 'Overrides', replaces: 'Option 1 overrides' },
  extraGuestSurcharges: { replaces: 'Extra adult and child surcharges' },
};

export const IMPORT_SECTIONS = IMPORT_SECTION_KEYS.map((key) => {
  const { label, replaces } = { ...COPY_SECTIONS.find((section) => section.key === key), ...OWN_SECTIONS[key] };
  return { key, label, replaces };
});

export function importReplaceWarning(selected: Set<ImportSectionKey>, replacesGrid = false): string | undefined {
  return replacesWarning(
    IMPORT_SECTIONS.filter(({ key }) => selected.has(key)).map(({ key, replaces }) =>
      key === 'surcharges' && replacesGrid ? `${replaces} and the weekday grid` : replaces,
    ),
  );
}

export type ImportStep = 'workbook' | 'propertySetup' | 'rooms' | 'inclusions' | 'confirm';

export const IMPORT_STEP_LABELS: Record<ImportStep, string> = {
  workbook: 'Workbook',
  propertySetup: 'Property Setup',
  rooms: 'Rooms',
  inclusions: 'Inclusions',
  confirm: 'Confirm',
};

export function importSteps(selected: Set<ImportSectionKey>): Array<ImportStep> {
  return [
    'workbook',
    ...(selected.has('propertySetup') ? ['propertySetup' as const] : []),
    ...(selected.has('dealOptions') ? ['rooms' as const] : []),
    ...(selected.has('inclusions') ? ['inclusions' as const] : []),
    'confirm',
  ];
}

// Borrows the account view's rollout flag (off in prod) until the import has a flag of its own.
export function isExcelImportEnabled(): boolean {
  return featureToggle.availableToShow('ACCOUNT_VIEW_ENABLED');
}

// Only unnamed blackouts are numbered, in the same order as the parser's notes about them.
export function toBlackoutFormValues(blackouts: ReadonlyArray<ParsedBlackout>): Array<BlackoutFormValues> {
  let unnamed = 0;
  return blackouts.map(({ name, startDate, endDate }) => ({
    name: name ?? `Blackout ${(unnamed += 1)}`,
    startDate,
    endDate,
  }));
}

// Numbered among unnamed periods only, matching the parser's notes. Cost needs the workbook's currency to mean anything.
export function toSurchargePeriods(
  periods: ReadonlyArray<ParsedSurchargePeriod>,
  workbookCurrency: string | null,
  sessionCurrency: string,
): Array<SurchargePeriodFormValues> {
  let unnamed = 0;
  return periods.map(({ name, startDate, endDate, sellAud, cost }) => ({
    ...NEW_PERIOD_DEFAULTS,
    name: name ?? `Period ${(unnamed += 1)}`,
    startDate,
    endDate,
    sellAud,
    cost: workbookCurrency ? cost : null,
    currency: workbookCurrency ?? sessionCurrency,
  }));
}

// Errors with a cell are entries the parser rejected; one without a cell means the block itself is missing.
function rejectedCount(workbook: ParsedWorkbook, section: ImportSection): number {
  return workbook.issues.filter((issue) => issue.section === section && issue.severity === 'error' && issue.cell)
    .length;
}

function sectionError(workbook: ParsedWorkbook, section: ImportSection): ImportIssue | undefined {
  return workbook.issues.find((issue) => issue.section === section && issue.severity === 'error' && !issue.cell);
}

// Rows with the same problem share one note, so a sheet of broken names reads as one line rather than eleven.
function issueNotes(workbook: ParsedWorkbook, section: ImportSection): CopySectionDetails['notes'] {
  const blocking = sectionError(workbook, section);
  const issues = workbook.issues.filter((issue) => issue.section === section && issue !== blocking);
  const groups = Object.values(groupBy(issues, (issue) => `${issue.row ? 'rows' : 'own'}::${issue.message}`));
  return groups.map((group) => {
    const rows = group.flatMap(({ row }) => (row ? [String(row)] : []));
    const where = rows.length ? `${rows.length === 1 ? 'Row' : 'Rows'} ${andList(rows)} won't load: ` : '';
    return { severity: 'warning' as const, text: `${where}${group[0].message}` };
  });
}

function emptyMessage(workbook: ParsedWorkbook, section: ImportSection, skipped: string, none: string): string {
  return sectionError(workbook, section)?.message ?? (skipped ? `${skipped} found, none will load.` : none);
}

function withSkipped(summary: string, skipped: number, noun?: string): string {
  return skipped ? `${summary}, ${noun ? pluralizeToString(noun, skipped) : skipped} won't` : summary;
}

const STALE_RANGE_TEXT = {
  outsideTravel: "is outside this Opportunity's travel dates",
  ended: 'has already ended',
};

// Property Setup fills in a few seconds after the page loads; until then only the "already ended" check runs.
const NO_TRAVEL_WINDOW: TravelWindow = { travelFrom: null, travelTo: null };

// Unlike Copy from previous, stale ranges still load: in the Opportunity's own workbook they are likely typos.
export function blackoutsDetails(
  workbook: ParsedWorkbook,
  travelWindow: TravelWindow | null,
  today: string,
): CopySectionDetails {
  const notes = issueNotes(workbook, 'blackouts');
  const skipped = rejectedCount(workbook, 'blackouts');
  if (workbook.blackouts.length === 0) {
    const found = skipped ? pluralizeToString('blackout', skipped) : '';
    return { emptyMessage: emptyMessage(workbook, 'blackouts', found, 'The workbook has no blackouts.'), notes };
  }
  toBlackoutFormValues(workbook.blackouts).forEach((blackout) => {
    const reason = staleRangeReason(blackout, travelWindow ?? NO_TRAVEL_WINDOW, today);
    if (reason) {
      const dates = `${formatDateShort(blackout.startDate)} to ${formatDateShort(blackout.endDate)}`;
      notes.push({
        severity: 'warning',
        text: `The "${blackout.name}" blackout (${dates}) ${STALE_RANGE_TEXT[reason]}. It still loads, so check it on the Blackouts tab.`,
      });
    }
  });
  return {
    summary: withSkipped(`${pluralizeToString('blackout', workbook.blackouts.length)} will load`, skipped),
    notes,
  };
}

// Periods add up on a night they share, as Excel's Days Surcharged counts them; usually an end date one day late.
function sharedNightNotes(periods: Array<SurchargePeriodFormValues>): Array<string> {
  return periods.flatMap((period, index) =>
    periods.slice(index + 1).flatMap((other) => {
      const from = period.startDate > other.startDate ? period.startDate : other.startDate;
      const to = period.endDate < other.endDate ? period.endDate : other.endDate;
      if (from > to) {
        return [];
      }
      const when =
        from === to
          ? `on ${formatDateShort(from)}, so that night adds`
          : `from ${periodDates({ startDate: from, endDate: to })}, so those nights add`;
      return [
        `The "${period.name}" and "${other.name}" periods both apply ${when} both. They load as Accumulate Yes; set it to No on the Surcharges tab if only the larger one should apply.`,
      ];
    }),
  );
}

function hasGridContent({ sellAud, costLocal, seasonByMonth }: ParsedSurchargeGrid): boolean {
  return (
    Object.keys(seasonByMonth).length > 0 ||
    SEASON_GRIDS.some(({ key }) => WEEKDAYS.some((day) => sellAud[key][day] > 0 || costLocal[key][day] != null))
  );
}

export function tabHasGrid({ grids, costs }: SurchargeSessionState): boolean {
  return hasGridContent({ sellAud: grids, costLocal: costs, seasonByMonth: grids.seasonByMonth });
}

// An empty grid in the workbook leaves the tab's alone; most templates are left all zero.
export function loadableSurchargeGrid(
  workbook: ParsedWorkbook,
  sessionCurrency: string,
): (Pick<SurchargeSessionState, 'costs'> & { grids: Omit<SurchargeGrids, 'periods'> }) | null {
  const grid = workbook.surchargeGrid;
  if (!grid || !hasGridContent(grid)) {
    return null;
  }
  // Grid costs carry no currency of their own: they are in the session's, like the tab's.
  const withCosts = loadsCosts(workbook, sessionCurrency);
  return {
    grids: { ...grid.sellAud, seasonByMonth: grid.seasonByMonth },
    costs: withCosts ? grid.costLocal : createEmptySurchargeSession().costs,
  };
}

function weekdaySellText(amounts: WeekdayAmounts): string | null {
  const days = WEEKDAYS.filter((day) => amounts[day] > 0);
  if (days.length === 0) {
    return null;
  }
  return Object.entries(groupBy(days, (day) => amounts[day]))
    .map(([, sameDays]) =>
      sameDays.length === WEEKDAYS.length
        ? `${audMoney(amounts[sameDays[0]])} every day`
        : `${audMoney(amounts[sameDays[0]])} on ${andList(sameDays.map((day) => WEEKDAY_LABELS[day]))}`,
    )
    .join(', ');
}

function gridParts({ sellAud, seasonByMonth }: ParsedSurchargeGrid): Array<string> {
  const sells = SEASON_GRIDS.flatMap(({ key, label }) => {
    const text = weekdaySellText(sellAud[key]);
    return text ? [`${label}: ${text}`] : [];
  });
  const months = (['high', 'shoulder'] as const).flatMap((season) => {
    const keys = Object.keys(seasonByMonth).filter((month) => seasonByMonth[month] === season);
    const labels = travelMonthsFrom(keys.map((month) => `${month}-01`)).map(({ label }) => label);
    return labels.length ? [`${upperFirst(season)} in ${andList(labels)}`] : [];
  });
  const parts = [...sells, ...months];
  return parts.length ? parts : ['costs only'];
}

function gridCostsNote(workbook: ParsedWorkbook, sessionCurrency: string): string | null {
  const grid = workbook.surchargeGrid;
  const hasCosts = !!grid && SEASON_GRIDS.some(({ key }) => WEEKDAYS.some((day) => grid.costLocal[key][day] != null));
  if (!hasCosts || loadsCosts(workbook, sessionCurrency)) {
    return null;
  }
  return costsNote(workbook, sessionCurrency, 'Weekday costs');
}

export function surchargesDetails(
  workbook: ParsedWorkbook,
  travelWindow: TravelWindow | null,
  today: string,
  { sessionCurrency, keepsTabGrid }: { sessionCurrency: string; keepsTabGrid: boolean },
): CopySectionDetails {
  const notes = issueNotes(workbook, 'surcharges');
  const skipped = rejectedCount(workbook, 'surcharges');
  const grid = workbook.surchargeGrid && hasGridContent(workbook.surchargeGrid) ? workbook.surchargeGrid : null;
  const gridNote = grid ? gridCostsNote(workbook, sessionCurrency) : null;
  if (gridNote) {
    notes.push({ severity: 'warning', text: gridNote });
  }
  if (workbook.surchargePeriods.length === 0) {
    if (grid) {
      return { summary: 'The weekday grid will load', notes };
    }
    const found = skipped ? pluralizeToString('period', skipped) : '';
    return {
      emptyMessage: emptyMessage(
        workbook,
        'surcharges',
        found,
        'The workbook has no surcharge periods or weekday surcharges.',
      ),
      notes,
    };
  }
  if (!grid && keepsTabGrid) {
    notes.push({
      severity: 'info',
      text: "The workbook's weekday grid is empty, so the grid on Surcharges stays as it is.",
    });
  }
  const { currency } = workbook.propertySetup;
  const periods = toSurchargePeriods(workbook.surchargePeriods, currency, DEFAULT_CURRENCY);
  if (!currency && workbook.surchargePeriods.some(({ cost }) => cost !== null)) {
    notes.push({ severity: 'warning', text: costsNote(workbook, sessionCurrency, 'Costs') });
  }
  periods.forEach((period) => {
    const reason = staleRangeReason(period, travelWindow ?? NO_TRAVEL_WINDOW, today);
    if (reason) {
      notes.push({
        severity: 'warning',
        text: `The "${period.name}" period (${periodDates(period)}) ${STALE_RANGE_TEXT[reason]}. It still loads, so check it on the Surcharges tab.`,
      });
    }
  });
  sharedNightNotes(periods).forEach((text) => notes.push({ severity: 'warning', text }));
  const count = pluralizeToString('period', periods.length);
  const summary = grid ? `${count} and the weekday grid will load` : `${count} will load`;
  return { summary: withSkipped(summary, skipped), notes };
}

// The detail Confirm lists under the summary, so the PCM sees what replaces the tab before Load.
export function surchargesConfirmLines(workbook: ParsedWorkbook): Array<string> {
  const periods = toSurchargePeriods(workbook.surchargePeriods, workbook.propertySetup.currency, DEFAULT_CURRENCY);
  const grid = workbook.surchargeGrid && hasGridContent(workbook.surchargeGrid) ? workbook.surchargeGrid : null;
  return [
    ...periods.map((period) => `${period.name}: ${periodDates(period)} at ${audMoney(period.sellAud)}`),
    ...(grid ? gridParts(grid).map((part) => `Weekday grid: ${part}`) : []),
  ];
}

export type PropertySetupField = 'travelDates' | 'currency' | 'fxRate' | 'country';

export type FxSource = 'workbook' | 'today';

export interface PropertySetupPicks {
  fields: Record<PropertySetupField, boolean>;
  fxSource: FxSource;
}

// Only rows that differ can be ticked, so ticking everything is "differing fields start on the workbook".
export const DEFAULT_PROPERTY_SETUP_PICKS: PropertySetupPicks = {
  fields: { travelDates: true, currency: true, fxRate: true, country: true },
  fxSource: 'workbook',
};

export interface FxOption {
  source: FxSource;
  rate: number | null;
  label: string;
}

export interface PropertySetupRow {
  field: PropertySetupField;
  label: string;
  session: string;
  workbook: string;
  // differs: the PCM ticks it. forced: the FX that comes with a switched currency. note: shown, never loads.
  status: 'differs' | 'forced' | 'same' | 'note';
  note?: string;
  // What the Load column says for a note row: the session's value is kept, or the workbook's can't be used.
  keeps?: 'Kept' | 'Not listed';
  fxOptions?: Array<FxOption>;
  fxDifference?: string;
  // The source that loads: the picked one, or today's when the workbook has no rate.
  fxSource?: FxSource;
  changes: Partial<PropertySetupValues>;
}

const NOT_SET = 'Not set';

function travelText(travelFrom: string | null, travelTo: string | null): string {
  return travelFrom && travelTo ? `${formatDateShort(travelFrom)} to ${formatDateShort(travelTo)}` : NOT_SET;
}

// Display only: the full rate still loads, as Auto sets it, and the difference line is worked out from it.
function rateText(rate: number | null): string {
  return rate === null ? NOT_SET : rate.toLocaleString('en-AU', { maximumFractionDigits: 3 });
}

function fxDifferenceText(workbookRate: number | null, todayRate: number | null): string | undefined {
  if (!workbookRate || !todayRate || workbookRate === todayRate) {
    return undefined;
  }
  const percent = Math.abs(((todayRate - workbookRate) / workbookRate) * 100).toFixed(1);
  return `Today's rate is ${percent}% ${todayRate < workbookRate ? 'below' : 'above'} the workbook's.`;
}

function compared(
  field: PropertySetupField,
  label: string,
  [session, workbook]: [string, string],
  same: boolean,
  changes: Partial<PropertySetupValues>,
): PropertySetupRow {
  return { field, label, session, workbook, status: same ? 'same' : 'differs', changes };
}

function pickedCurrency(parsed: ParsedPropertySetup, session: PropertySetupValues, picks: PropertySetupPicks) {
  return picks.fields.currency && parsed.currency ? parsed.currency : session.currency;
}

// The workbook's Cost Prices were converted at its own rate, so that rate is offered first.
function fxRow(
  parsed: ParsedPropertySetup,
  session: PropertySetupValues,
  picks: PropertySetupPicks,
  todayRateFor: (currency: string) => number | null,
): PropertySetupRow | null {
  const currency = pickedCurrency(parsed, session, picks);
  if (!currency || currency === DEFAULT_CURRENCY) {
    return null;
  }
  const switched = currency !== session.currency;
  // A workbook rate is for the workbook's currency, so it is no use when the session keeps another.
  const workbookRate = currency === parsed.currency ? parsed.fxRate : null;
  if (!switched && workbookRate === null) {
    return null;
  }
  const texts: [string, string] = [rateText(session.fxRate), rateText(workbookRate)];
  if (!switched && workbookRate === session.fxRate) {
    return compared('fxRate', 'FX rate', texts, true, {});
  }
  const todayRate = todayRateFor(currency);
  const fxOptions: Array<FxOption> = [
    ...(workbookRate === null
      ? []
      : [
          {
            source: 'workbook' as const,
            rate: workbookRate,
            label: `The workbook's ${rateText(workbookRate)}${parsed.fxNote ? ` (${parsed.fxNote})` : ''}`,
          },
        ]),
    {
      source: 'today',
      rate: todayRate,
      label: todayRate === null ? "Today's rate (not available)" : `Today's ${rateText(todayRate)}`,
    },
  ];
  const picked = fxOptions.find(({ source }) => source === picks.fxSource) ?? fxOptions[0];
  return {
    ...compared('fxRate', 'FX rate', texts, false, { fxRate: picked.rate }),
    status: switched ? 'forced' : 'differs',
    fxOptions,
    fxDifference: fxDifferenceText(workbookRate, todayRate),
    fxSource: picked.source,
  };
}

// Writing a country clears destination and sub-destination, which the workbook can't refill, so it only fills a gap.
function countryRow(parsed: ParsedPropertySetup, session: PropertySetupValues): PropertySetupRow | null {
  if (!parsed.country) {
    return null;
  }
  const match = findLocationCountry(parsed.country, session.dealCategory);
  const texts: [string, string] = [session.offerCountry ?? NOT_SET, parsed.country];
  if (session.offerCountry && match === session.offerCountry) {
    return compared('country', 'Country', texts, true, {});
  }
  if (!session.offerCountry && match) {
    const changes = applyLocationChange(pick(session, LOCATION_FIELDS), 'offerCountry', match);
    return compared('country', 'Country', texts, false, changes);
  }
  return {
    ...compared('country', 'Country', texts, false, {}),
    status: 'note',
    keeps: session.offerCountry ? 'Kept' : 'Not listed',
    note: session.offerCountry
      ? `The workbook says "${parsed.country}", Property Setup has ${session.offerCountry}.`
      : `The workbook's country "${parsed.country}" isn't one Property Setup lists, so it won't load.`,
  };
}

export function propertySetupRows(
  parsed: ParsedPropertySetup,
  session: PropertySetupValues,
  picks: PropertySetupPicks,
  todayRateFor: (currency: string) => number | null,
): Array<PropertySetupRow> {
  const { travelFrom, travelTo, currency } = parsed;
  const sessionCurrency = session.currency ?? DEFAULT_CURRENCY;
  return [
    travelFrom && travelTo
      ? compared(
          'travelDates',
          'Travel dates',
          [travelText(session.travelFrom, session.travelTo), travelText(travelFrom, travelTo)],
          travelFrom === session.travelFrom && travelTo === session.travelTo,
          { travelFrom, travelTo },
        )
      : null,
    currency
      ? compared('currency', 'Currency', [sessionCurrency, currency], currency === sessionCurrency, {
          currency,
          // Matches setCurrency, which AUD always resolves to 1.
          ...(currency === DEFAULT_CURRENCY ? { fxRate: 1 } : {}),
        })
      : null,
    fxRow(parsed, session, picks, todayRateFor),
    countryRow(parsed, session),
  ].filter((row): row is PropertySetupRow => row !== null);
}

export function hasPropertySetupToLoad(rows: ReadonlyArray<PropertySetupRow>): boolean {
  return rows.some(({ status }) => status === 'differs' || status === 'forced');
}

export function loadedPropertySetupRows(
  rows: ReadonlyArray<PropertySetupRow>,
  picks: PropertySetupPicks,
): Array<PropertySetupRow> {
  return rows.filter(({ field, status }) => status === 'forced' || (status === 'differs' && picks.fields[field]));
}

export function propertySetupChanges(
  rows: ReadonlyArray<PropertySetupRow>,
  picks: PropertySetupPicks,
): Partial<PropertySetupValues> {
  return Object.assign({}, ...loadedPropertySetupRows(rows, picks).map(({ changes }) => changes));
}

export function propertySetupDetails(
  workbook: ParsedWorkbook,
  rows: ReadonlyArray<PropertySetupRow>,
  picks: PropertySetupPicks,
): CopySectionDetails {
  const notes = [
    ...issueNotes(workbook, 'propertySetup'),
    ...rows.flatMap(({ note }) => (note ? [{ severity: 'info' as const, text: note }] : [])),
  ];
  if (!hasPropertySetupToLoad(rows)) {
    return {
      emptyMessage: rows.length
        ? 'Property Setup already matches the workbook.'
        : "The workbook's header has nothing Property Setup can use.",
      emptySeverity: 'info',
      notes,
    };
  }
  // The FX that loads can be today's rate rather than the workbook's.
  const loading = loadedPropertySetupRows(rows, picks).map(
    ({ field, label, workbook: value, changes }) =>
      `${label.replace(/^[A-Z](?=[a-z])/, (letter) => letter.toLowerCase())} (${field === 'fxRate' ? rateText(changes.fxRate ?? null) : value})`,
  );
  if (loading.length === 0) {
    return { summary: 'Nothing ticked to load', notes };
  }
  const summary = andList(loading);
  return { summary: `${summary.charAt(0).toUpperCase()}${summary.slice(1)} will load`, notes };
}

// A currency switch is session-wide, so Confirm spells out what it takes with it.
export function currencySwitchWarnings(
  session: PropertySetupValues,
  picked: PropertySetupValues,
  {
    hasTypedSurchargeCosts,
    keepsNets,
    keepsEgsCosts,
    rrpReason,
  }: { hasTypedSurchargeCosts: boolean; keepsNets: boolean; keepsEgsCosts: boolean; rrpReason: string | null },
): Array<string> {
  const from = session.currency ?? DEFAULT_CURRENCY;
  const to = picked.currency ?? DEFAULT_CURRENCY;
  if (from === to) {
    return [];
  }
  return [
    ...(hasTypedSurchargeCosts ? [`Switching to ${to} clears the surcharge costs typed in ${from}.`] : []),
    // Package Options and the extra guest surcharges re-label their costs rather than converting them.
    ...(keepsNets
      ? [
          `Switching to ${to} keeps the net prices on Package Options as typed, so a net of 500 ${from} becomes 500 ${to}. Check them on Package Options.`,
        ]
      : []),
    ...(keepsEgsCosts
      ? [
          `Switching to ${to} keeps the extra guest surcharge costs as typed, so a cost of 50 ${from} becomes 50 ${to}. Check them on the Extra Guest Surcharges tab.`,
        ]
      : []),
    ...(rrpReason ? [rrpReason] : []),
  ];
}

export function hasTypedNets(states: ReadonlyArray<OptionState>): boolean {
  return states.some(({ pricingByRoomId }) =>
    Object.values(pricingByRoomId).some(
      ({ netByLos, extraNightNet }) =>
        Object.values(netByLos).some((net) => Number(net) > 0) || Number(extraNightNet) > 0,
    ),
  );
}

export function loadsCosts({ propertySetup }: ParsedWorkbook, netCurrency: string): boolean {
  return propertySetup.currency === netCurrency;
}

export function importedRoomTypes(dealOptions: ReadonlyArray<ParsedDealOption>): Array<string> {
  return uniq(dealOptions.map(({ roomType }) => roomType));
}

const PICK_CURRENCY = 'Tick Property Setup and its currency to load them.';

function costsNote(
  { propertySetup: { currency } }: ParsedWorkbook,
  sessionCurrency: string,
  subject = 'Net prices',
): string {
  return currency
    ? `${subject} won't load: the workbook is in ${currency} and this session is in ${sessionCurrency}. ${PICK_CURRENCY}`
    : `${subject} won't load: the workbook doesn't say which currency they're in.`;
}

function mixedOccupancyText(roomTypes: Array<string>): string {
  const one = roomTypes.length === 1;
  const rooms = andList(roomTypes.map((roomType) => `"${roomType}"`));
  return `${rooms} ${one ? 'has' : 'have'} a different occupancy for each stay, so ${one ? 'it loads' : 'they load'} as ${DEFAULT_OCCUPANCY}. Check ${one ? 'it' : 'them'} on Package Options.`;
}

export function dealOptionsDetails(workbook: ParsedWorkbook, netCurrency: string): CopySectionDetails {
  const notes = issueNotes(workbook, 'dealOptions');
  const skipped = rejectedCount(workbook, 'dealOptions');
  if (workbook.dealOptions.length === 0) {
    const found = skipped ? pluralizeToString('row', skipped) : '';
    const none = 'The Deal Options Import sheet has no base packages.';
    return { emptyMessage: emptyMessage(workbook, 'dealOptions', found, none), notes };
  }
  if (workbook.skippedVariants.length > 0) {
    // Grouped the way base names are matched, so "IndulgenceUpgrade" counts with "Indulgence Upgrade".
    const variants = Object.values(
      groupBy(workbook.skippedVariants, (variant) => variant.replace(/\s+/g, '').toLowerCase()),
    )
      .map((labels) => `${labels[0]} (${labels.length})`)
      .join(', ');
    notes.push({
      severity: 'info',
      text: `Only base packages load. Add the others on Package Options afterwards: ${variants}.`,
    });
  }
  const mixed = mixedOccupancyRoomTypes(workbook.dealOptions);
  if (mixed.length) {
    notes.push({ severity: 'warning', text: mixedOccupancyText(mixed) });
  }
  if (!loadsCosts(workbook, netCurrency)) {
    notes.push({ severity: 'warning', text: costsNote(workbook, netCurrency) });
  }
  const rooms = pluralizeToString('room', importedRoomTypes(workbook.dealOptions).length);
  const prices = pluralizeToString('price', workbook.dealOptions.length);
  return { summary: withSkipped(`${rooms} will load (${prices})`, skipped, 'row'), notes };
}

// The grid holds one occupancy per row, so a room whose stays name different ones keeps the default.
function roomOccupancy(rows: Array<ParsedDealOption>): string | undefined {
  const occupancies = uniq(rows.map(({ occupancy }) => occupancy ?? DEFAULT_OCCUPANCY));
  return occupancies.length === 1 ? occupancies[0] : undefined;
}

function rowsByRoomType(dealOptions: ReadonlyArray<ParsedDealOption>) {
  return Object.entries(groupBy(dealOptions, 'roomType'));
}

function mixedOccupancyRoomTypes(dealOptions: ReadonlyArray<ParsedDealOption>): Array<string> {
  return rowsByRoomType(dealOptions).flatMap(([roomType, rows]) => (roomOccupancy(rows) ? [] : [roomType]));
}

export function toImportedDealOptions(
  dealOptions: ReadonlyArray<ParsedDealOption>,
  assetByRoomType: Record<string, RoomAssetLink>,
  withCosts: boolean,
): PreviousDealOptions {
  const packages = rowsByRoomType(dealOptions).map(([roomType, rows]) => {
    // The tool holds one allocation and extra night price per row, taken from the shortest stay.
    const shortestStay = minBy(rows, 'nights');
    return {
      asset: assetByRoomType[roomType],
      categoryIndex: BASIC_CATEGORY_INDEX,
      dealIdByLos: {},
      occupancy: roomOccupancy(rows) ?? DEFAULT_OCCUPANCY,
      allocation: shortestStay.allocation,
      sellByLos: Object.fromEntries(rows.map(({ nights, sell }) => [nights, sell])),
      netByLos: withCosts
        ? Object.fromEntries(rows.flatMap(({ nights, net }) => (net === null ? [] : [[nights, net]])))
        : {},
      extraNightSell: shortestStay.extraNightSell ?? undefined,
      extraNightNet: withCosts ? (shortestStay.extraNightNet ?? undefined) : undefined,
    };
  });
  return {
    packages,
    ambiguousCells: 0,
    mixedOccupancyPackages: mixedOccupancyRoomTypes(dealOptions).length,
    otherCurrencyNetCells: 0,
  };
}

const WORKBOOK_RRP_CURRENCY = 'AUD';

export function loadsRrp(sessionCurrency: string): boolean {
  return sessionCurrency === WORKBOOK_RRP_CURRENCY;
}

export function rrpNotLoadedReason(workbook: ParsedWorkbook, sessionCurrency: string): string | null {
  return !loadsRrp(sessionCurrency) && workbook.inclusions.some(({ rrp }) => rrp !== null)
    ? `RRP won't load in a ${sessionCurrency} session: the workbook gives it in ${WORKBOOK_RRP_CURRENCY}.`
    : null;
}

export function inclusionsDetails(
  workbook: ParsedWorkbook,
  sessionCurrency: string,
  leftOutRows: ReadonlySet<number> = new Set(),
): CopySectionDetails {
  const notes = issueNotes(workbook, 'inclusions');
  const skipped = rejectedCount(workbook, 'inclusions');
  if (workbook.inclusions.length === 0) {
    const found = skipped ? pluralizeToString('row', skipped) : '';
    const none = 'The Inclusions Import sheet has no inclusions.';
    return { emptyMessage: emptyMessage(workbook, 'inclusions', found, none), notes };
  }
  const loading = workbook.inclusions.filter(({ row }) => !leftOutRows.has(row));
  const rrpReason = rrpNotLoadedReason(workbook, sessionCurrency);
  if (rrpReason) {
    notes.push({ severity: 'warning', text: `${rrpReason} Add it on Inclusions before saving.` });
  } else {
    const withoutRrp = loading.filter(({ rrp }) => rrp === null).length;
    if (withoutRrp) {
      notes.push({
        severity: 'info',
        text: `${pluralizeToString('inclusion', withoutRrp)} ${withoutRrp === 1 ? 'has' : 'have'} no RRP in the workbook. Add it on Inclusions before saving.`,
      });
    }
  }
  const summary = withSkipped(`${pluralizeToString('inclusion', loading.length)} will load`, skipped, 'row');
  return { summary: leftOutRows.size ? `${summary}, ${leftOutRows.size} left out` : summary, notes };
}

export function toInclusionDrafts(
  inclusions: ReadonlyArray<ParsedInclusion>,
  withRrp: boolean,
): Array<InclusionFormValues> {
  return inclusions.map(({ name, type, description, prv, rrp }) => ({
    pricingOptionIndex: 0,
    name,
    description: description ?? '',
    type: type ?? undefined,
    validFor: [],
    rrp: withRrp ? (rrp ?? undefined) : undefined,
    prv: prv ?? undefined,
    applyTo: defaultApplyTo(),
  }));
}

export type InclusionAssetChoices = Record<number, string | null>;

// Null is a new asset. Same lookup the step shows, so a refetch after review cannot change what Load links.
export function reviewInclusionAssets(
  inclusions: ReadonlyArray<ParsedInclusion>,
  sessionAssets: Array<CreatedAsset>,
  accountAssets: Array<RoomAssetLink>,
): InclusionAssetChoices {
  return Object.fromEntries(
    inclusions.map(({ row, name }) => [
      row,
      findReusableInclusionAsset(name, sessionAssets, accountAssets)?.id ?? null,
    ]),
  );
}

export function inclusionRowsWithMissingAsset(
  choices: InclusionAssetChoices,
  sessionAssets: Array<CreatedAsset>,
  accountAssets: Array<RoomAssetLink>,
): Array<number> {
  const listed = new Set([...sessionAssets, ...accountAssets].map(({ id }) => id));
  return Object.entries(choices)
    .filter(([, assetId]) => assetId !== null && !listed.has(assetId))
    .map(([row]) => Number(row));
}

export function missingAssetMessages(roomTypes: Array<string>, inclusionNames: Array<string>): Array<string> {
  return [
    ...(roomTypes.length
      ? [`The room picked for ${andList(roomTypes)} is no longer on this property. Go back to Rooms and pick again.`]
      : []),
    ...(inclusionNames.length
      ? [
          `The asset for ${andList(uniq(inclusionNames))} is no longer on this property. Go back to Inclusions and check again.`,
        ]
      : []),
  ];
}

// Vendor revenue tiers are amounts in the workbook's currency, but the forecast compares them with revenue in the session's.
function tiersInOtherCurrency(workbook: ParsedWorkbook, overrides: ParsedOverrides, sessionCurrency: string): boolean {
  return overrides.basis === 'vendor_revenue' && workbook.propertySetup.currency !== sessionCurrency;
}

// The sections a Currency untick on the Property Setup step can stop from loading.
export type CurrencyBoundSection = Extract<ImportSectionKey, 'overrides' | 'extraGuestSurcharges'>;

const CURRENCY_BOUND_NOUNS: Record<CurrencyBoundSection, string> = {
  overrides: 'overrides',
  extraGuestSurcharges: 'extra guest surcharges',
};

function skipPhrase(sections: ReadonlyArray<CurrencyBoundSection>): string {
  return `skip ${sections.length > 1 ? 'both' : CURRENCY_BOUND_NOUNS[sections[0]]}`;
}

export function skipSectionsLabel(sections: ReadonlyArray<CurrencyBoundSection>): string {
  return upperFirst(skipPhrase(sections));
}

// On the Property Setup step the way back is the Currency tick right above, not Property Setup itself.
export function currencyPickNote(
  { propertySetup: { currency } }: ParsedWorkbook,
  sessionCurrency: string,
  sections: ReadonlyArray<CurrencyBoundSection>,
): string {
  const nouns = upperFirst(andList(sections.map((section) => CURRENCY_BOUND_NOUNS[section])));
  return `${nouns} are in ${currency}, so they won't load while the session stays in ${sessionCurrency}. Tick Currency to load them, or ${skipPhrase(sections)}.`;
}

function overridesCurrencyNote({ propertySetup: { currency } }: ParsedWorkbook, sessionCurrency: string): string {
  return currency
    ? `Overrides won't load: the tiers are in ${currency} and this session is in ${sessionCurrency}. ${PICK_CURRENCY}`
    : "Overrides won't load: the workbook doesn't say which currency the tiers are in.";
}

export function loadableOverrides(workbook: ParsedWorkbook, sessionCurrency: string): ParsedOverrides | null {
  const { overrides } = workbook;
  if (!overrides || overrides === 'off' || overrides.tiers.length === 0) {
    return null;
  }
  return tiersInOtherCurrency(workbook, overrides, sessionCurrency) ? null : overrides;
}

export function toOverrideSet({ basis, tiers }: ParsedOverrides): OverrideSet {
  return {
    basis,
    tiers: tiers.map(({ from, to, comms }, index) => ({
      id: crypto.randomUUID(),
      name: defaultTierName(index),
      from: from ?? undefined,
      to,
      comms,
    })),
  };
}

function tierText({ from, to, comms }: ParsedOverrideTier): string {
  const range =
    from === null
      ? `up to ${to.toLocaleString('en-AU')}`
      : `${from.toLocaleString('en-AU')} to ${to.toLocaleString('en-AU')}`;
  return `${range} at ${comms}%`;
}

export function overridesDetails(workbook: ParsedWorkbook, sessionCurrency: string): CopySectionDetails {
  const notes = issueNotes(workbook, 'overrides');
  const parsed = workbook.overrides;
  if (parsed === 'off') {
    return {
      emptyMessage: 'Overrides are off in the workbook (Basis is None), so nothing will load.',
      emptySeverity: 'info',
      notes,
    };
  }
  if (parsed && parsed.tiers.length > 0 && tiersInOtherCurrency(workbook, parsed, sessionCurrency)) {
    return { emptyMessage: overridesCurrencyNote(workbook, sessionCurrency), notes };
  }
  const overrides = loadableOverrides(workbook, sessionCurrency);
  if (!overrides) {
    return { emptyMessage: emptyMessage(workbook, 'overrides', '', 'The Overrides section has no tiers.'), notes };
  }
  // The same rules the save checks, so a tier it would refuse is flagged before loading.
  const [invalid] = validateOverrides(toOverrideSet(overrides));
  if (invalid) {
    notes.push({ severity: 'warning', text: `${invalid} Fix it on Overrides before saving.` });
  }
  const basis = BASIS_OPTIONS.find(({ value }) => value === overrides.basis).label.toLowerCase();
  const tiers = pluralizeToString('tier', overrides.tiers.length);
  return { summary: `${tiers} by ${basis} will load (${overrides.tiers.map(tierText).join(', ')})`, notes };
}

const IMPORTED_EGS_GUESTS = EGS_CATS.filter(
  (meta): meta is EgsCategoryMeta & { key: Exclude<EgsGuestCategoryKey, 'infant'> } => meta.key !== 'infant',
);
// Ages the workbook doesn't give, the same range Copy from previous fills when Salesforce leaves it blank.
const IMPORTED_EGS_AGES = { fromAge: '0', toAge: '99' };

// Workbook room name to the asset its extra guest surcharges go on, or null when it has none.
export type EgsRoomAssets = Record<string, RoomAssetLink | null>;

// With Deal Options the Rooms step already matched these names; otherwise each takes its closest Option 1 room.
export function egsRoomAssets(
  rooms: ReadonlyArray<string>,
  dealOptionAssets: Record<string, RoomAssetLink> | null,
  optionRooms: Array<RoomAssetLink>,
): EgsRoomAssets {
  if (dealOptionAssets) {
    const byName = new Map(Object.entries(dealOptionAssets).map(([roomType, asset]) => [nameKey(roomType), asset]));
    return Object.fromEntries(rooms.map((room) => [room, byName.get(nameKey(room)) ?? null]));
  }
  const choices = prefillRoomChoices(rooms, optionRooms);
  return Object.fromEntries(rooms.map((room) => [room, optionRooms.find(({ id }) => id === choices[room]) ?? null]));
}

// A record's cost is in the session's currency, so costs in another one would load as the wrong amount.
export function loadableEgs(workbook: ParsedWorkbook, sessionCurrency: string): Array<ParsedEgsRoom> {
  return loadsCosts(workbook, sessionCurrency) ? workbook.extraGuestSurcharges : [];
}

// One record per guest type and price, over every room that shares it, so the PCM finishes each price once.
function egsGroups(rooms: ReadonlyArray<ParsedEgsRoom>, roomAssets: EgsRoomAssets) {
  return IMPORTED_EGS_GUESTS.flatMap((meta) => {
    const priced = rooms.flatMap((room) => {
      const price = room[meta.key];
      const asset = roomAssets[room.room];
      return price && asset ? [{ price, asset }] : [];
    });
    return Object.values(groupBy(priced, ({ price }) => `${price.cost}::${price.sell}`)).map((group) => ({
      meta,
      price: group[0].price,
      assetIds: uniq(group.map(({ asset }) => asset.id)),
    }));
  });
}

// The workbook holds no infants, so the ones on the tab stay through a load.
export function keepsOnEgsLoad({ guestType }: Pick<EgsDraft, 'guestType'>): boolean {
  return guestType === 'Extra Infant';
}

// The rooms stay listed even when they cover Option 1: submit reads "all" as every room of the preferred option.
export function toEgsRecords(
  rooms: ReadonlyArray<ParsedEgsRoom>,
  roomAssets: EgsRoomAssets,
  currency: string,
  rate: number | undefined,
): Array<EgsDraftInput> {
  return egsGroups(rooms, roomAssets).map(({ meta, price, assetIds }) =>
    buildEgsRecord(
      {
        ...defaultEgsCategoryDraft(),
        ...IMPORTED_EGS_AGES,
        costPrice: String(price.cost),
        sellPriceAud: String(price.sell),
      },
      meta,
      assetIds,
      currency,
      { [currency]: rate },
    ),
  );
}

const NO_EGS_LOADS = "None of the workbook's extra guest surcharges will load.";

function unmatchedEgsRoomsNote(rooms: Array<string>, fromDealOptions: boolean): string {
  const one = rooms.length === 1;
  const names = andList(rooms.map((room) => `"${room}"`));
  const where = fromDealOptions
    ? `${one ? "isn't a room type" : "aren't room types"} in the Deal Options Import sheet`
    : `${one ? 'matches' : 'match'} no room on Package Options Option 1`;
  return `${names} ${where}, so ${one ? 'its' : 'their'} extra guest surcharges won't load.`;
}

function egsRoomText(room: string, asset: RoomAssetLink): string {
  return nameKey(room) === nameKey(asset.name) ? `"${room}"` : `"${room}" as ${asset.name}`;
}

export function egsDetails(
  workbook: ParsedWorkbook,
  sessionCurrency: string,
  roomAssets: EgsRoomAssets,
  { fromDealOptions, hasRate }: { fromDealOptions: boolean; hasRate: boolean },
): CopySectionDetails {
  const notes = issueNotes(workbook, 'extraGuestSurcharges');
  const parsed = workbook.extraGuestSurcharges;
  if (parsed.length === 0) {
    const none = notes.length ? NO_EGS_LOADS : 'The workbook has no extra guest surcharges.';
    return { emptyMessage: sectionError(workbook, 'extraGuestSurcharges')?.message ?? none, notes };
  }
  if (!loadsCosts(workbook, sessionCurrency)) {
    return { emptyMessage: costsNote(workbook, sessionCurrency, 'Extra guest surcharges'), notes };
  }
  if (!hasRate) {
    return {
      emptyMessage: `Extra guest surcharges won't load: there's no ${sessionCurrency} FX rate to work out their margin.`,
      notes,
    };
  }
  const unmatched = parsed.filter(({ room }) => !roomAssets[room]).map(({ room }) => room);
  if (unmatched.length) {
    notes.push({ severity: 'warning', text: unmatchedEgsRoomsNote(unmatched, fromDealOptions) });
  }
  const matched = parsed.filter(({ room }) => roomAssets[room]);
  if (matched.length === 0) {
    return { emptyMessage: NO_EGS_LOADS, notes };
  }
  notes.push({
    severity: 'info',
    text: 'Set ages, bedding, rate type and inclusions on the Extra Guest Surcharges tab.',
  });
  const count = `${pluralizeToString('extra guest surcharge', egsGroups(matched, roomAssets).length)} for ${pluralizeToString('room', matched.length)}`;
  // Without the Rooms step nobody has confirmed the match yet, so the summary shows it.
  const summary = fromDealOptions
    ? `${count} will load`
    : `${count} will load (${matched.map(({ room }) => egsRoomText(room, roomAssets[room])).join(', ')})`;
  return { summary: withSkipped(summary, rejectedCount(workbook, 'extraGuestSurcharges'), 'price'), notes };
}
